import React from "react";
import { Search, User, ChevronRight, ChevronLeft, Info } from "lucide-react";

const EmployeeListStep = ({
  loading,
  employees,
  empSearch,
  setEmpSearch,
  onSelect,
  currentTheme,
  t,
  isDark,
  isArabic,
}) => {
  const styles = {
    container: {
      display: "flex",
      flexDirection: "column",
      gap: "20px",
    },
    searchContainer: {
      position: "relative",
      width: "100%",
    },
    searchInput: {
      width: "100%",
      paddingBlock: "14px",
      paddingInlineStart: "44px",
      paddingInlineEnd: "16px",
      borderRadius: "16px",
      border: `1px solid ${currentTheme.colors.border}`,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
      color: currentTheme.colors.textPrimary,
      fontSize: "15px",
      outline: "none",
      transition: "all 0.2s ease",
    },
    searchIcon: {
      position: "absolute",
      insetInlineStart: "14px",
      top: "50%",
      transform: "translateY(-50%)",
      color: currentTheme.colors.textSecondary,
    },
    list: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },
    item: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 20px",
      borderRadius: "18px",
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.01)",
      border: `1px solid ${currentTheme.colors.border}`,
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    itemHover: {
      background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.03)",
      borderColor: currentTheme.colors.primary + "40",
      transform: "translateY(-1px)",
    },
    empInfo: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
    },
    avatar: {
      width: "44px",
      height: "44px",
      borderRadius: "12px",
      background: currentTheme.colors.primary + "15",
      color: currentTheme.colors.primary,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    name: {
      fontSize: "16px",
      fontWeight: "700",
      color: currentTheme.colors.textPrimary,
      margin: 0,
    },
    subText: {
      fontSize: "13px",
      color: currentTheme.colors.textSecondary,
      margin: 0,
    },
    stats: {
      display: "flex",
      alignItems: "center",
      gap: "24px",
    },
    statItem: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
    },
    statValue: {
      fontSize: "15px",
      fontWeight: "700",
      color: currentTheme.colors.primary,
    },
    statLabel: {
      fontSize: "11px",
      color: currentTheme.colors.textSecondary,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
  };

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: currentTheme.colors.textSecondary }}>
        <div style={{ marginBottom: "12px" }}>{t("miLoadingEmployees")}</div>
        {/* Simple spinner placeholder */}
        <div style={{ width: "30px", height: "30px", border: "3px solid transparent", borderTopColor: currentTheme.colors.primary, borderRadius: "50%", margin: "0 auto", animation: "spin 1s linear infinite" }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.searchContainer}>
        <Search size={20} style={styles.searchIcon} />
        <input
          style={styles.searchInput}
          placeholder={t("miSearchPlaceholder")}
          value={empSearch}
          onChange={(e) => setEmpSearch(e.target.value)}
        />
      </div>

      <div style={styles.list}>
        {employees.length > 0 ? (
          employees.map((emp) => (
            <div
              key={emp.infinityCode}
              style={styles.item}
              onClick={() => onSelect(emp)}
              onMouseEnter={(e) => {
                Object.assign(e.currentTarget.style, styles.itemHover);
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = styles.item.background;
                e.currentTarget.style.borderColor = styles.item.border;
                e.currentTarget.style.transform = "none";
              }}
            >
              <div style={styles.empInfo}>
                <div style={styles.avatar}>
                  <User size={24} />
                </div>
                <div>
                  <h4 style={styles.name}>{isArabic ? emp.arabicName || emp.name : emp.name || emp.arabicName}</h4>
                  <p style={styles.subText}>{emp.clientCode || emp.infinityCode} • {emp.department || t("noDepartment")}</p>
                </div>
              </div>

              <div style={styles.stats}>
                <div style={styles.statItem}>
                  <span style={styles.statValue}>{emp.familyMemberCount || 0}</span>
                  <span style={styles.statLabel}>{t("miMembers")}</span>
                </div>
                <div style={styles.statItem}>
                  <span style={{ ...styles.statValue, color: currentTheme.colors.success }}>
                    {emp.totalMonthlyInsurance ? emp.totalMonthlyInsurance.toLocaleString() : 0}
                  </span>
                  <span style={styles.statLabel}>{t("miMonthlyCost")}</span>
                </div>
                {isArabic ? (
                  <ChevronLeft size={20} color={currentTheme.colors.textSecondary} />
                ) : (
                  <ChevronRight size={20} color={currentTheme.colors.textSecondary} />
                )}
              </div>
            </div>
          ))
        ) : (
          <div style={{ padding: "40px", textAlign: "center", color: currentTheme.colors.textSecondary }}>
            <Info size={40} style={{ marginBottom: "16px", opacity: 0.5 }} />
            <p>{t("miNoEmployees")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeListStep;